import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type Method
} from 'axios'
import { NabooError } from '@/core/infrastructure/services/NabooError'
import HttpResponse from '@/core/infrastructure/services/HttpResponse.ts'

// Interface pour l'abstraction de l'authentification
export interface AuthProvider {
  isAuthenticated(): boolean
  getAccessToken(): Promise<string | null>
  refreshToken(): Promise<string | null>
  redirectToLogin(): Promise<void>
}

export interface ApiServiceConfig {
  baseURL: string
  timeout?: number
  maxRetries?: number
  retryDelay?: number
}

export default class ApiService {
  private readonly axiosInstance: AxiosInstance
  private readonly authProvider: AuthProvider
  private readonly maxRetries: number
  private readonly retryDelay: number
  private retriedRequests = new Set<string>() // Pour éviter les boucles infinies

  constructor(
    authProvider: AuthProvider,
    config: ApiServiceConfig,
    customAxiosInstance?: AxiosInstance
  ) {
    this.authProvider = authProvider
    this.maxRetries = config.maxRetries ?? 1
    this.retryDelay = config.retryDelay ?? 1000

    this.axiosInstance =
      customAxiosInstance ||
      axios.create({
        baseURL: config.baseURL,
        timeout: config.timeout ?? 10000,
        // Ajouter des headers par défaut
        headers: {
          'Content-Type': 'application/json'
        }
      })

    this.setupInterceptors()
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        const token = await this.authProvider.getAccessToken()
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(this.handleError(error))
    )

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const requestId = this.getRequestId(error)

        // Gestion du 401 avec protection contre les boucles infinies
        if (this.shouldRetryAuth(error, requestId)) {
          return this.handleAuthRetry(error, requestId)
        }

        return Promise.reject(this.handleError(error))
      }
    )
  }

  private shouldRetryAuth(error: AxiosError, requestId: string): boolean {
    return (
      error.response?.status === 401 &&
      !this.retriedRequests.has(requestId) &&
      this.authProvider.isAuthenticated()
    )
  }

  private async handleAuthRetry(error: AxiosError, requestId: string): Promise<any> {
    // Marquer cette requête comme "retry en cours"
    this.retriedRequests.add(requestId)

    try {
      // Essayer de rafraîchir le token
      const newToken = await this.authProvider.refreshToken()

      if (newToken && error.config?.headers) {
        error.config.headers.Authorization = `Bearer ${newToken}`

        // Attendre avant de retry
        await this.delay(this.retryDelay)

        // Retry la requête
        const response = await this.axiosInstance.request(error.config)
        this.retriedRequests.delete(requestId)
        return response
      }
    } catch (refreshError) {
      console.error('Échec du refresh token:', refreshError)
    }

    // Si le refresh échoue, rediriger vers login
    this.retriedRequests.delete(requestId)
    await this.authProvider.redirectToLogin()
    return Promise.reject(this.handleError(error))
  }

  private getRequestId(error: AxiosError): string {
    // Créer un ID unique basé sur l'URL et la méthode
    const method = error.config?.method || 'unknown'
    const url = error.config?.url || 'unknown'
    return `${method}:${url}`
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * Méthode request "native" pour plus de flexibilité si besoin
   * @param url
   * @param method
   * @param data
   * @param config
   */
  public async request<T = unknown, D = unknown>(
    url: string,
    method: Method,
    data?: D,
    config?: AxiosRequestConfig<D>
  ): Promise<HttpResponse<T>> {
    const response = await this.axiosInstance.request<T, AxiosResponse<T>, D>({
      url,
      method,
      data,
      ...config
    })
    return new HttpResponse<T>(response)
  }

  // Méthodes HTTP publiques (simplifié, car intercepteur gère les erreurs)
  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<HttpResponse<T>> {
    const response = await this.axiosInstance.get<T>(url, config)
    return new HttpResponse<T>(response)
  }

  public async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<HttpResponse<T>> {
    const response = await this.axiosInstance.post<T>(url, data, config)
    return new HttpResponse<T>(response)
  }

  public async put<T>(url: string, data: unknown, config?: AxiosRequestConfig): Promise<HttpResponse<T>> {
    const response = await this.axiosInstance.put<T>(url, data, config)
    return new HttpResponse<T>(response)
  }

  public async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<HttpResponse<T>> {
    const response = await this.axiosInstance.patch<T>(url, data, config)
    return new HttpResponse<T>(response)
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<HttpResponse<T>> {
    const response = await this.axiosInstance.delete<T>(url, config)
    return new HttpResponse<T>(response)
  }

  // Méthodes utilitaires
  public setDefaultHeader(key: string, value: string): void {
    this.axiosInstance.defaults.headers.common[key] = value
  }

  public removeDefaultHeader(key: string): void {
    delete this.axiosInstance.defaults.headers.common[key]
  }

  private handleError(error: unknown): NabooError {
    if (axios.isAxiosError(error)) {
      return NabooError.fromAxiosError(error)
    }

    return new NabooError({
      message: 'An unknown error occurred',
      status: 500,
      name: 'UnknownError',
      code: 'UNKNOWN_ERROR'
    })
  }
}
